import React from 'react';
import { CloudServerOutlined, DatabaseOutlined, DollarOutlined, SafetyOutlined } from '@ant-design/icons';
import { ExecutivePresentation, InventoryRow, SummaryMetric, TabSection } from './types';
import {
  buildSecurityRiskItems,
  buildSecurityRiskSubtitle,
  buildProjects,
  buildServices,
  numberFormatter,
  normalizeStatusTone,
  resolveCustomerLabel,
  toArray
} from './shared';

const buildCommitmentSummary = (data: any): SummaryMetric | null => {
  const commitment = data?.commitmentScenarios;
  const officialRecommendations = toArray(commitment?.officialRecommendations);
  if (!commitment || !officialRecommendations.length) {
    return null;
  }

  return {
    title: 'CUD Recommender',
    count: `${officialRecommendations.length} recomendação(ões)`,
    value: Number(commitment.officialMonthlySavings || 0),
    subtitle: 'Economia oficial lida do Google Recommender/FinOps Hub.',
    accent: '#16a34a',
    icon: <DollarOutlined />,
    drilldownKey: 'commitment'
  };
};

const buildSummaryMetrics = (data: any): SummaryMetric[] => [
  buildCommitmentSummary(data),
  {
    title: 'XKnow AI Independente',
    count: !data?.xknowOptimizationAnalysis
      ? 'Não executada'
      : data.xknowOptimizationAnalysis.status === 'unavailable'
        ? 'Indisponível'
        : data.xknowOptimizationAnalysis.summary?.opportunities || 0,
    value: data?.xknowOptimizationAnalysis?.summary?.probable_monthly_savings || 0,
    subtitle: !data?.xknowOptimizationAnalysis
      ? 'Este snapshot foi gerado sem executar a análise independente. Atualize o relatório para recalcular.'
      : data.xknowOptimizationAnalysis.status === 'unavailable'
        ? 'O serviço de IA não respondeu nesta coleta. Atualize novamente para tentar recalcular.'
        : 'Análise própria por métricas e custos; sobreposições com o Google são marcadas e não somadas.',
    accent: '#0891b2',
    icon: <DollarOutlined />,
    drilldownKey: 'xknowOptimization'
  },
  {
    title: 'Cenário Máximo Não Validado',
    // Quando não há um array real de oportunidades (data?.strategicSavingsPotential),
    // isso é uma projeção de benchmark top-down, não uma lista contável de
    // itens - mostrar um número fixo (8) aqui era um valor fabricado sem
    // relação com o ambiente real do cliente.
    count:
      toArray(data?.strategicSavingsPotential?.opportunities).length ||
      (Number(data?.overview?.strategicAnnualSavingsEstimate || 0) > 0 ? 'Projeção' : 0),
    value:
      data?.strategicSavingsPotential?.estimatedMonthlySavings ||
      Number(data?.overview?.strategicAnnualSavingsEstimate || 0) / 12 ||
      0,
    subtitle: 'Teto teórico por benchmark; não é economia identificada, recomendação oficial ou compromisso comercial.',
    accent: '#7c3aed',
    icon: <DollarOutlined />,
    drilldownKey: 'strategicPotential'
  },
  {
    title: 'Otimizações Oficiais',
    count: data?.optimizationOpportunities?.total || 0,
    value: data?.optimizationOpportunities?.estimatedMonthlySavings || 0,
    subtitle: 'Rightsizing e recursos ociosos lidos do Google Recommender.',
    accent: '#16a34a',
    icon: <DollarOutlined />,
    drilldownKey: 'officialOptimizations'
  },
  {
    title: 'Recursos Inventariados',
    count: data?.inventory?.resources || 0,
    subtitle: 'Inventário lido via Cloud Asset Inventory.',
    accent: '#2563eb',
    icon: <CloudServerOutlined />,
    drilldownKey: 'inventory'
  },
  {
    title: 'Storage e Dados',
    count: (data?.inventory?.buckets || 0) + (data?.inventory?.sqlInstances || 0),
    subtitle: 'Buckets e bancos analisados para exposição e controles.',
    accent: '#f59e0b',
    icon: <DatabaseOutlined />,
    drilldownKey: 'inventory'
  },
  {
    title: 'Alertas de Segurança',
    count: data?.executiveSummary?.securityAlerts?.total || 0,
    subtitle: 'Riscos de exposição, criptografia e governança.',
    accent: '#ef4444',
    icon: <SafetyOutlined />,
    drilldownKey: 'security'
  }
].filter(Boolean) as SummaryMetric[];

const buildCommitmentItems = (data: any) => {
  const commitment = data?.commitmentScenarios;
  const officialRecommendations = toArray(commitment?.officialRecommendations);
  if (!commitment || !officialRecommendations.length) {
    return [];
  }

  return officialRecommendations.map((recommendation, index) => ({
    id: recommendation.id || `gcp-cud-recommender-${index}`,
    name: recommendation.name || `Recomendação CUD Google ${index + 1}`,
    type: recommendation.recommender || 'Google Recommender',
    badge: 'Oficial Google',
    badgeTone: 'success' as const,
    description: [
      recommendation.description || 'Recomendação oficial do Google Recommender para compromisso de uso.',
      `Economia mensal estimada em ${numberFormatter(Number(recommendation.monthlySavings || 0), 'gcp')} e anual em ${numberFormatter(Number(recommendation.annualSavings || 0), 'gcp')}.`,
      'A xKnow usa essa recomendação como base e prioriza a execução com contexto de billing, inventário e risco.'
    ].join(' '),
    meta: [
      recommendation.subtype,
      recommendation.durationMonths ? `Duração projetada: ${recommendation.durationMonths} mês(es)` : undefined,
      recommendation.scope
    ].filter(Boolean).join(' • '),
    cost: `Mensal: ${numberFormatter(Number(recommendation.monthlySavings || 0), 'gcp')} • Anual: ${numberFormatter(Number(recommendation.annualSavings || 0), 'gcp')}`,
    sourceLabel: 'Google Recommender',
    sourceTone: 'success' as const
  }));
};

const buildOptimizationItems = (items: any[], fallbackType: string) => toArray(items).map((item, index) => ({
  id: item.id || `gcp-optimization-${index}`,
  name: item.name || item.description || `Recomendação GCP ${index + 1}`,
  type: item.recommender || fallbackType,
  badge: item.priority || 'Oficial Google',
  badgeTone: normalizeStatusTone(item.priority || 'success'),
  description: [
    item.description || 'Recomendação oficial do Google Recommender.',
    Number(item.monthlySavings || 0) > 0
      ? `Economia mensal estimada em ${numberFormatter(Number(item.monthlySavings || 0), 'gcp')} e anual em ${numberFormatter(Number(item.annualSavings || 0), 'gcp')}.`
      : 'A recomendação não trouxe estimativa financeira no payload oficial.'
  ].join(' '),
  meta: [
    item.resourceName,
    item.subtype,
    item.scope
  ].filter(Boolean).join(' • '),
  cost: Number(item.monthlySavings || 0) > 0
    ? `Mensal: ${numberFormatter(Number(item.monthlySavings || 0), 'gcp')} • Anual: ${numberFormatter(Number(item.annualSavings || 0), 'gcp')}`
    : undefined,
  sourceLabel: 'Google Recommender',
  sourceTone: 'success' as const
}));

const strategicReductionGuide = (item: any) => {
  const id = String(item?.id || '');
  const lever = String(item?.lever || '');
  const key = `${id} ${lever}`.toLowerCase();

  // O nome do cenário de CUD contém "após rightsizing". CUD precisa ser
  // identificado antes para não herdar a orientação operacional de VMs.
  if (/cud|commit/.test(key)) {
    return 'Como reduzir: primeiro concluir o rightsizing; depois medir a utilização mínima recorrente de vCPU e memória em uma janela representativa, simular diferentes níveis de compromisso e contratar Resource CUD somente para a parcela estável e previsível, mantendo picos e cargas sazonais fora do compromisso.';
  }
  if (/rightsizing|ocioso/.test(key)) {
    return 'Como reduzir: identificar VMs com baixa utilização, validar CPU e memória em uma janela representativa e então reduzir o machine type, desligar ou remover os recursos confirmados como ociosos.';
  }
  if (/logging|retenção|retencao|exclusão|exclusao/.test(key)) {
    return 'Como reduzir: localizar logs de alto volume e baixo valor, criar filtros de exclusão, ajustar a retenção por bucket e rotear apenas os registros que precisam de armazenamento longo.';
  }
  if (/network|egress|nat|interzona/.test(key)) {
    return 'Como reduzir: mapear egress, NAT e tráfego entre zonas, aproximar produtores e consumidores, eliminar transferências evitáveis e revisar topologia, cache e gateways.';
  }
  if (/storage|lifecycle|autoclass|versões|versoes/.test(key)) {
    return 'Como reduzir: aplicar Lifecycle ou Autoclass, expirar versões e objetos temporários, migrar dados frios para classes adequadas e remover buckets ou objetos sem uso.';
  }
  if (/monitoring|cardinalidade/.test(key)) {
    return 'Como reduzir: identificar métricas e labels de alta cardinalidade, remover dimensões sem uso, limitar métricas customizadas e ajustar períodos de retenção e coleta.';
  }
  if (/cloud run|concorrência|concorrencia|instâncias mínimas|instancias minimas/.test(key)) {
    return 'Como reduzir: ajustar concorrência e limites de CPU/memória, revisar CPU fora de requisição, reduzir instâncias mínimas e validar autoscaling com a carga real.';
  }
  if (/kubernetes|gke|bin-packing|autoscaling|spot/.test(key)) {
    return 'Como reduzir: melhorar o bin-packing, ajustar requests e limits, habilitar autoscaling e mover workloads tolerantes a interrupção para Spot após validação.';
  }
  return 'Como reduzir: validar a composição do serviço, identificar os recursos responsáveis pelo custo e executar a alavanca indicada em um piloto antes de expandir a mudança.';
};

const formatClosedMonth = (month: string) => {
  const match = /^(\d{4})-(\d{2})$/.exec(String(month || ''));
  if (!match) return String(month || '');
  return new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
};

const strategicBasePeriod = (data: any) => {
  const months = toArray(data?.overview?.monthlyAverageCalculation?.includedMonths)
    .map(formatClosedMonth)
    .filter(Boolean);
  if (months.length === 1) return `Base referente a ${months[0]}.`;
  if (months.length > 1) return `Base mensal média dos meses fechados: ${months.join(', ')}.`;
  return 'Período da base não identificado no snapshot.';
};

const buildStrategicPotentialItems = (data: any) =>
  toArray(data?.strategicSavingsPotential?.opportunities).map((item, index) => {
    const isCommitmentScenario = /cud|commit/i.test(`${item?.id || ''} ${item?.lever || ''}`);
    const baseMonthlyCost = Number(item.baseMonthlyCost || 0);
    const rate = Number(item.rate || 0);
    const estimatedMonthlySavings = Number(item.estimatedMonthlySavings || 0);
    const annualSavings = Number(item.annualSavings || 0);
    return {
    id: item.id || `gcp-strategic-${index}`,
    name: `${item.service}: ${item.lever}`,
    type: 'Hipótese de otimização não validada',
    badge: 'Estimativa por benchmark • baixa confiança',
    badgeTone: 'warning' as const,
    description: [
      isCommitmentScenario
        ? `Base mensal elegível estimada após rightsizing: ${numberFormatter(baseMonthlyCost, 'gcp')}.`
        : `Base mensal observada do serviço: ${numberFormatter(baseMonthlyCost, 'gcp')}.`,
      strategicBasePeriod(data),
      isCommitmentScenario
        ? `Desconto de referência usado na simulação: ${(rate * 100).toFixed(0)}%.`
        : `Premissa de redução usada na simulação: ${(rate * 100).toFixed(0)}%.`,
      `Fórmula do teto teórico: ${numberFormatter(baseMonthlyCost, 'gcp')} × ${(rate * 100).toFixed(0)}% = ${numberFormatter(estimatedMonthlySavings, 'gcp')} por mês.`,
      strategicReductionGuide(item),
      'Este valor não é uma recomendação oficial nem economia identificada. Valide utilização, SKUs, elegibilidade e regras comerciais antes de apresentá-lo ao cliente.'
    ].join(' '),
    cost: `Até ${numberFormatter(estimatedMonthlySavings, 'gcp')}/mês em cenário teórico • até ${numberFormatter(annualSavings, 'gcp')}/ano`,
    sourceLabel: 'Modelo xKnow por benchmark',
    sourceTone: 'warning' as const,
    allowFeedback: false
  };
  });

const buildXknowOptimizationItems = (data: any) =>
  toArray(data?.xknowOptimizationAnalysis?.opportunities).map((item, index) => ({
    id: item.id || `gcp-xknow-${index}`,
    name: item.title || item.resource_id || `Oportunidade XKnow ${index + 1}`,
    type: item.category || 'Otimização',
    badge: `Confiança ${Math.round(Number(item.confidence || 0) * 100)}%`,
    badgeTone: normalizeStatusTone(item.confidence_label),
    description: [
      toArray(item.evidence).join(' • '),
      item.overlaps_provider ? 'Também recomendada pelo Google; não somar novamente.' : 'Sem sobreposição oficial identificada.',
      item.next_action
    ].filter(Boolean).join(' '),
    meta: item.resource_id || 'Análise por serviço',
    cost: `Provável: ${numberFormatter(Number(item.probable_monthly_savings || 0), 'gcp')}/mês`,
    sourceLabel: 'XKnow AI independente',
    sourceTone: 'info' as const
  }));

const buildTabs = (data: any): TabSection[] => {
  const commitmentItems = buildCommitmentItems(data);
  const rightsizingItems = buildOptimizationItems(data?.upsizingAnalysis?.recommendations, 'Rightsizing');
  const idleItems = buildOptimizationItems(data?.idleResourcesDetails, 'Recurso ocioso');
  const strategicPotentialItems = buildStrategicPotentialItems(data);
  const xknowOptimizationItems = buildXknowOptimizationItems(data);
  const security = toArray(data?.securityDetails).map((item, index) => {
    const firstDetail = toArray(item.details)[0];
    return {
      name: item.title || `Alerta GCP ${index + 1}`,
      type: item.affected ? `${item.affected} recurso(s)` : 'Security',
      badge: item.severity || 'Média',
      badgeTone: normalizeStatusTone(item.severity),
      description: firstDetail
        ? `${firstDetail.issue}. ${firstDetail.recommendation}.`
        : item.description || item.recommendation || 'Revisar configuração de segurança.',
      meta: firstDetail ? [firstDetail.resourceType, firstDetail.location].filter(Boolean).join(' • ') : undefined,
      sourceLabel: 'Cloud Asset Inventory',
      sourceTone: 'success' as const
    };
  });

  const prioritizedRisks = toArray(data?.riskPrioritization?.prioritized_findings).map((item, index) => ({
    name: item.title || `Risco priorizado ${index + 1}`,
    type: item.resource_type || 'Cloud Risk',
    badge: `${Math.round(item.risk_score || 0)}/100`,
    badgeTone: normalizeStatusTone(item.priority),
    description: [
      item.business_impact,
      item.why_now,
      item.recommendation ? `Ação: ${item.recommendation}` : undefined
    ].filter(Boolean).join(' '),
    meta: [
      item.priority ? `Prioridade ${item.priority}` : undefined,
      item.effort ? `Esforço ${item.effort}` : undefined,
      item.confidence ? `Confiança ${Math.round(item.confidence * 100)}%` : undefined
    ].filter(Boolean).join(' • '),
    sourceLabel: 'Cloud Risk Prioritization AI',
    sourceTone: 'success' as const
  }));
  const securityRiskAnalysis = buildSecurityRiskItems(data);

  const inventorySummary = [
    { key: 'compute', name: 'Compute Engine', count: data?.inventory?.computeInstances || 0, type: 'VMs' },
    { key: 'disks', name: 'Persistent Disks', count: data?.inventory?.disks || 0, type: 'Discos' },
    { key: 'storage', name: 'Cloud Storage', count: data?.inventory?.buckets || 0, type: 'Buckets' },
    { key: 'sql', name: 'Cloud SQL', count: data?.inventory?.sqlInstances || 0, type: 'Bancos' },
    { key: 'gke', name: 'Google Kubernetes Engine', count: data?.inventory?.gkeClusters || 0, type: 'Clusters' }
  ]
    .filter(item => item.count > 0)
    .map(item => ({
      name: item.name,
      type: item.type,
      badge: `${item.count}`,
      badgeTone: 'info' as const,
      description: 'Grupo de recursos incluído na análise de postura GCP.',
      sourceLabel: 'Cloud Asset Inventory',
      sourceTone: 'success' as const
    }));

  const inventory = inventorySummary;

  return [
    {
      key: 'xknowOptimization',
      label: 'XKnow AI',
      subtitle: 'Recomendações independentes produzidas em paralelo à IA do Google, com evidência, confiança e deduplicação.',
      items: xknowOptimizationItems
    },
    {
      key: 'strategicPotential',
      label: 'Cenário não validado',
      subtitle: 'Teto teórico de planejamento baseado no custo por serviço. Pode se sobrepor às recomendações oficiais e não constitui economia identificada.',
      items: strategicPotentialItems
    },
    {
      key: 'commitment',
      label: 'CUD',
      subtitle: 'Recomendações oficiais de compromisso retornadas pelo Google Recommender.',
      items: commitmentItems
    },
    {
      key: 'rightsizing',
      label: 'Rightsizing',
      subtitle: 'Recomendações oficiais para ajuste de capacidade retornadas pelo Google Recommender.',
      items: rightsizingItems
    },
    {
      key: 'idle',
      label: 'Ociosos',
      subtitle: 'Recursos ociosos ou removíveis retornados pelo Google Recommender.',
      items: idleItems
    },
    {
      key: 'security-ai',
      label: 'IA de Segurança',
      subtitle: buildSecurityRiskSubtitle(data),
      items: securityRiskAnalysis
    },
    {
      key: 'risk-ai',
      label: 'Priorização IA',
      subtitle: data?.riskPrioritization?.executive_summary || 'Riscos ordenados por impacto, exposição e esforço de correção.',
      items: prioritizedRisks
    },
    {
      key: 'security',
      label: 'Segurança',
      subtitle: 'Alertas de postura, exposição e governança do projeto GCP.',
      items: security
    },
    {
      key: 'inventory',
      label: 'Inventário',
      subtitle: 'Resumo dos recursos encontrados no projeto conectado.',
      items: inventory
    }
  ].filter(section => section.items.length > 0);
};

const buildInventoryRows = (data: any): InventoryRow[] => {
  const inventory = data?.inventory || {};
  return [
    { name: 'Recursos', count: inventory.resources || 0 },
    { name: 'Compute Instances', count: inventory.computeInstances || 0 },
    { name: 'Persistent Disks', count: inventory.disks || 0 },
    { name: 'Cloud Storage Buckets', count: inventory.buckets || 0 },
    { name: 'Cloud SQL Instances', count: inventory.sqlInstances || 0 },
    { name: 'GKE Clusters', count: inventory.gkeClusters || 0 }
  ];
};

const gcpServiceInventoryMatcher = (serviceName: string, resource: any) => {
  const service = String(serviceName || '').toLowerCase();
  const assetType = String(resource?.assetType || resource?.type || '').toLowerCase();
  const category = String(resource?.category || '').toLowerCase();

  if (service.includes('compute engine')) return category === 'compute';
  if (service.includes('network')) return category === 'network';
  if (service.includes('cloud logging')) return assetType.startsWith('logging.googleapis.com/');
  if (service.includes('cloud monitoring')) return category === 'observability' || assetType.startsWith('monitoring.googleapis.com/');
  if (service.includes('cloud storage')) return category === 'storage' || assetType.startsWith('storage.googleapis.com/');
  if (service.includes('kubernetes')) return category === 'containers' || assetType.includes('k8s.io/');
  if (service.includes('cloud run functions')) return assetType.startsWith('cloudfunctions.googleapis.com/');
  if (service === 'cloud run' || service.includes('cloud run')) return assetType.startsWith('run.googleapis.com/');
  if (service.includes('bigquery')) return assetType.startsWith('bigquery.googleapis.com/');
  if (service.includes('secret manager')) return assetType.startsWith('secretmanager.googleapis.com/');
  if (service.includes('artifact registry')) return assetType.startsWith('artifactregistry.googleapis.com/');
  if (service.includes('pub/sub')) return assetType.startsWith('pubsub.googleapis.com/');
  if (service.includes('cloud dns')) return assetType.startsWith('dns.googleapis.com/');
  return false;
};

const buildGcpServices = (data: any) => {
  const inventoryDetails = toArray(data?.inventoryDetails);
  return buildServices(data).map(service => {
    const matchedResources = inventoryDetails
      .filter(resource => gcpServiceInventoryMatcher(service.name, resource))
      .sort((left, right) => {
        const leftCost = left.monthlyCost == null ? -1 : Number(left.monthlyCost || 0);
        const rightCost = right.monthlyCost == null ? -1 : Number(right.monthlyCost || 0);
        return rightCost - leftCost || String(left.name || '').localeCompare(String(right.name || ''), 'pt-BR');
      });
    return {
      ...service,
      resourceCount: matchedResources.length,
      resources: matchedResources.slice(0, 30).map(resource => ({
        name: resource.name || resource.resourceId || 'Recurso GCP',
        type: resource.assetType || resource.type || resource.category,
        status: resource.status,
        region: resource.location || resource.zone,
        monthlyCost: resource.monthlyCost == null ? null : Number(resource.monthlyCost),
        costSource: resource.costSource || 'inventory-only',
        note: resource.projectId ? `Projeto: ${resource.projectId}` : undefined
      }))
    };
  });
};

export const createGcpPresentation = (data: any): ExecutivePresentation => ({
  provider: 'gcp',
  title: 'GCP Security & FinOps',
  providerLabel: 'Google Cloud Platform',
  customerLabel: resolveCustomerLabel(data, data?.scope?.name || data?.account?.id),
  customerDomain: data?.customerDomain || data?.metadata?.customerDomain,
  customerLogoUrl: data?.customerLogoUrl || data?.metadata?.customerLogoUrl,
  scopeLabel: data?.scope?.name || data?.account?.id || 'Projeto GCP conectado',
  overview: {
    monthlyAverageCost: data?.overview?.monthlyAverageCost || 0,
    annualSavingsEstimate: data?.overview?.annualSavingsEstimate || 0,
    usageCost: data?.overview?.usageCost,
    creditCost: data?.overview?.creditCost,
    monthlyAverageCalculation: data?.overview?.monthlyAverageCalculation,
    currentMonthEstimate: data?.overview?.currentMonthEstimate || null
  },
  executiveSummary: {
    netMonthlySavings: data?.executiveSummary?.netMonthlySavings || 0,
    idleTotal: data?.executiveSummary?.idleResources?.total || 0,
    securityTotal: data?.executiveSummary?.securityAlerts?.total || 0
  },
  summaryMetrics: buildSummaryMetrics(data),
  services: buildGcpServices(data),
  projects: buildProjects(data),
  inventoryRows: buildInventoryRows(data),
  tabs: buildTabs(data),
  rawData: data
});
